import React, { useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import {
    DefaultButton,
    DetailsList,
    DetailsListLayoutMode,
    Dialog,
    DialogFooter,
    DialogType,
    IColumn,
    MessageBar,
    MessageBarType,
    Pivot,
    PivotItem,
    PrimaryButton,
    SelectionMode,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField,
} from '@fluentui/react';
import { ThemeContext } from '../../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../../Common/util/localStorageUtils';
import { CapabilityContext } from '../../../Common/components/Capabilities/CapabilityContext';
import { Capability } from '../../../Common/components/Capabilities/capability';
import { ClusterBanner } from './ClusterBanner';
import { SectionCard } from './SectionCard';
import { KustoTenantDropdown } from './KustoTenantDropdown';
import {
    cancelMyKustoAccessRequest,
    extractKustoError,
    KustoAccessStatus,
    KustoRequestStatus,
    KustoTenant,
    listKustoTenants,
    listMyKustoAccessRequests,
    lookupKustoAccessForRequest,
    MyKustoAccessRequest,
    requestKustoAccessRemoval,
    submitKustoAccessRequest,
} from '../../api/KustoAccessApi';
import {
    CORE_IDENTITY_URL,
    KUSTO_DATABASE as DATABASE,
} from './kustoAccessConfig';
import {
    getGuidError,
    isGuid,
    isValidDisplayName,
    MAX_DISPLAY_NAME_LENGTH,
    MAX_FREE_TEXT_LENGTH,
} from './kustoAccessValidation';
import { useKustoRequest } from './useKustoRequest';

/**
 * Self-service Kusto access page. Surfaces the user-access (Core Identity) flow, a lookup to check
 * whether an AAD application already has access, and a request queue for application access that an
 * SRM admin reviews.
 */
export const KustoAccessPage: React.FC = () => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);

    // Core Identity only exists in the public cloud, so the user-access flow is public-only.
    const capabilities = useContext(CapabilityContext);
    const inPublicCloud = capabilities.check(Capability.public);

    const [tenants, setTenants] = useState<KustoTenant[]>([]);
    const [tenantsLoading, setTenantsLoading] = useState(true);
    const [tenantsError, setTenantsError] = useState<string | null>(null);
    const [tenantRefresh, setTenantRefresh] = useState(0);

    useEffect(() => {
        const source = axios.CancelToken.source();
        let active = true;
        setTenantsLoading(true);
        setTenantsError(null);
        listKustoTenants(source.token)
            .then((result) => {
                if (active) {
                    setTenants(result.data ?? []);
                }
            })
            .catch((error) => {
                if (active && !axios.isCancel(error)) {
                    setTenants([]);
                    setTenantsError(extractKustoError(error));
                }
            })
            .finally(() => {
                if (active) {
                    setTenantsLoading(false);
                }
            });
        return () => {
            active = false;
            source.cancel();
        };
    }, [tenantRefresh]);

    // Lookup state.
    const [lookupAppId, setLookupAppId] = useState<string>('');
    const [lookupTenantId, setLookupTenantId] = useState<string>('');
    const [lookupResult, setLookupResult] = useState<KustoAccessStatus | null>(
        null
    );
    const lookup = useKustoRequest();
    const lookupBusy = lookup.busy;
    const lookupError = lookup.error;

    // Request state.
    const [reqAppId, setReqAppId] = useState<string>('');
    const [reqTenantId, setReqTenantId] = useState<string>('');
    const [reqName, setReqName] = useState<string>('');
    const [reqJustification, setReqJustification] = useState<string>('');
    const [reqMessage, setReqMessage] = useState<string | null>(null);
    const request = useKustoRequest();
    const reqBusy = request.busy;
    const reqError = request.error;

    // My requests.
    const [myRequests, setMyRequests] = useState<MyKustoAccessRequest[]>([]);
    const mine = useKustoRequest();
    const mineBusy = mine.busy;
    const mineError = mine.error;

    // Confirmation targets. Both actions are irreversible without another admin review.
    const [cancelTarget, setCancelTarget] =
        useState<MyKustoAccessRequest | null>(null);
    const [removalTarget, setRemovalTarget] =
        useState<MyKustoAccessRequest | null>(null);
    const [removalJustification, setRemovalJustification] =
        useState<string>('');

    const lookupValid =
        !tenantsLoading &&
        !tenantsError &&
        isGuid(lookupAppId) &&
        tenants.some((tenant) => tenant.tenantId === lookupTenantId);
    const requestValid =
        !tenantsLoading &&
        !tenantsError &&
        isGuid(reqAppId) &&
        tenants.some((tenant) => tenant.tenantId === reqTenantId) &&
        reqName.trim().length > 0 &&
        isValidDisplayName(reqName) &&
        reqJustification.trim().length > 0 &&
        reqJustification.trim().length <= MAX_FREE_TEXT_LENGTH;

    // Depend on the hook's stable `run` rather than the object, which is new each render.
    const { run: runMine } = mine;

    const loadMine = useCallback(
        () =>
            runMine(async () => {
                const result = await listMyKustoAccessRequests();
                setMyRequests(result.data ?? []);
            }),
        [runMine]
    );

    useEffect(() => {
        void loadMine();
    }, [loadMine]);

    const handleLookup = useCallback(
        () =>
            lookup.run(async () => {
                setLookupResult(null);
                const result = await lookupKustoAccessForRequest(
                    lookupAppId.trim(),
                    lookupTenantId.trim()
                );
                setLookupResult(result.data ?? { hasAccess: false });
            }),
        [lookup, lookupAppId, lookupTenantId]
    );

    const handleSubmit = useCallback(
        () =>
            request.run(async () => {
                setReqMessage(null);
                const result = await submitKustoAccessRequest({
                    applicationId: reqAppId.trim(),
                    tenantId: reqTenantId.trim(),
                    displayName: reqName.trim(),
                    justification: reqJustification.trim(),
                });
                setReqMessage(
                    result.message ?? 'Request submitted for review.'
                );
                await loadMine();
            }),
        [request, reqAppId, reqTenantId, reqName, reqJustification, loadMine]
    );

    const confirmCancel = useCallback(
        () =>
            runMine(async () => {
                const target = cancelTarget;
                setCancelTarget(null);
                if (target?.id) {
                    await cancelMyKustoAccessRequest(target.id);
                    await loadMine();
                }
            }),
        [runMine, loadMine, cancelTarget]
    );

    const confirmRemoval = useCallback(
        () =>
            runMine(async () => {
                const target = removalTarget;
                const reason = removalJustification.trim();
                setRemovalTarget(null);
                setRemovalJustification('');
                if (target?.id) {
                    await requestKustoAccessRemoval(target.id, reason);
                    await loadMine();
                }
            }),
        [runMine, loadMine, removalTarget, removalJustification]
    );

    const myRequestColumns: IColumn[] = [
        {
            key: 'requestedOn',
            name: 'Requested',
            fieldName: 'requestedOn',
            minWidth: 150,
            maxWidth: 180,
            isResizable: true,
            onRender: (item?: MyKustoAccessRequest) => (
                <span>
                    {item ? new Date(item.requestedOn).toLocaleString() : ''}
                </span>
            ),
        },
        {
            key: 'action',
            name: 'Action',
            fieldName: 'action',
            minWidth: 70,
            maxWidth: 90,
        },
        {
            key: 'displayName',
            name: 'Application',
            fieldName: 'displayName',
            minWidth: 160,
            isResizable: true,
            isMultiline: true,
            onRender: (item?: MyKustoAccessRequest) => (
                <Stack tokens={{ childrenGap: 4 }}>
                    <span title={item?.applicationId}>
                        {item?.displayName ?? item?.applicationId ?? '\u2014'}
                    </span>
                    {item?.failureMessage && (
                        <Text
                            block
                            styles={{
                                root: {
                                    color: theme.semanticColors.errorText,
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                },
                            }}
                        >
                            {item.failureMessage}
                        </Text>
                    )}
                </Stack>
            ),
        },
        {
            key: 'status',
            name: 'Status',
            fieldName: 'status',
            minWidth: 90,
            maxWidth: 110,
            onRender: (item?: MyKustoAccessRequest) => {
                const status = item?.status;
                const color =
                    status === KustoRequestStatus.approved
                        ? theme.semanticColors.successIcon
                        : status === KustoRequestStatus.denied
                        ? theme.semanticColors.errorText
                        : theme.palette.neutralSecondary;
                return <span style={{ color }}>{status}</span>;
            },
        },
        {
            key: 'myActions',
            name: '',
            minWidth: 150,
            onRender: (item?: MyKustoAccessRequest) => {
                if (!item) {
                    return null;
                }
                if (item.status === KustoRequestStatus.pending) {
                    return (
                        <DefaultButton
                            text="Cancel"
                            iconProps={{ iconName: 'Cancel' }}
                            onClick={() => setCancelTarget(item)}
                            disabled={mineBusy}
                        />
                    );
                }
                if (
                    item.status === KustoRequestStatus.approved &&
                    item.action === 'Grant'
                ) {
                    return (
                        <DefaultButton
                            text="Request removal"
                            iconProps={{ iconName: 'BlockedSite' }}
                            onClick={() => {
                                setRemovalTarget(item);
                                setRemovalJustification('');
                            }}
                            disabled={mineBusy}
                        />
                    );
                }
                return null;
            },
        },
    ];

    return (
        <>
            <div
                style={{
                    width: '100%',
                    maxWidth: '100%',
                    margin: '0 auto',
                    padding: 24,
                    boxSizing: 'border-box',
                }}
            >
                <Stack tokens={{ childrenGap: 20 }}>
                    <Stack
                        horizontal
                        horizontalAlign="space-between"
                        verticalAlign="start"
                        tokens={{ childrenGap: 16 }}
                    >
                        <Stack tokens={{ childrenGap: 4 }}>
                            <Text
                                variant="xxLarge"
                                styles={{ root: { fontWeight: 700 } }}
                            >
                                Kusto access
                            </Text>
                            <Text
                                variant="medium"
                                styles={{
                                    root: {
                                        color: theme.palette.neutralSecondary,
                                    },
                                }}
                            >
                                {`Self-service access to the ${DATABASE} database`}
                            </Text>
                        </Stack>
                        <DefaultButton
                            text="Kusto access docs"
                            href={process.env.REACT_APP_KUSTO_URL || undefined}
                            target="_blank"
                            disabled={!process.env.REACT_APP_KUSTO_URL}
                            iconProps={{ iconName: 'OpenInNewWindow' }}
                        />
                    </Stack>

                    <ClusterBanner />

                    {/* 1 — User access via Core Identity */}
                    <SectionCard
                        icon="Contact"
                        title="User access"
                        description={
                            inPublicCloud
                                ? 'For individual people — join via Core Identity.'
                                : 'For individual people — already granted in this cloud.'
                        }
                    >
                        {inPublicCloud ? (
                            <Stack tokens={{ childrenGap: 14 }}>
                                <Text variant="medium">
                                    Individual users get read access by joining
                                    the <strong>AzureBridgeKusto-Data</strong>{' '}
                                    entitlement. Your manager approves the
                                    request, and access propagates a few minutes
                                    later.
                                </Text>
                                <Stack horizontal>
                                    <DefaultButton
                                        text="Request in Core Identity"
                                        href={CORE_IDENTITY_URL}
                                        target="_blank"
                                        iconProps={{
                                            iconName: 'OpenInNewWindow',
                                        }}
                                    />
                                </Stack>
                                <Text
                                    variant="small"
                                    styles={{
                                        root: {
                                            color: theme.palette
                                                .neutralSecondary,
                                        },
                                    }}
                                >
                                    You can only request access for yourself,
                                    and only for users — not groups.
                                </Text>
                            </Stack>
                        ) : (
                            <Text variant="medium">
                                Every authorized user in this cloud already has
                                read access to the <strong>{DATABASE}</strong>{' '}
                                database, so there is nothing to request. Use{' '}
                                <strong>Application access</strong> below to
                                onboard a service.
                            </Text>
                        )}
                    </SectionCard>

                    {/* 2 — Application access: check, request, and track your own requests. */}
                    <SectionCard
                        icon="Database"
                        title="Application access"
                        description="Check whether an app already has access, or request it."
                    >
                        {tenantsLoading && (
                            <Spinner label="Loading supported tenants..." />
                        )}
                        {tenantsError && (
                            <MessageBar messageBarType={MessageBarType.error}>
                                {tenantsError}
                            </MessageBar>
                        )}
                        {!tenantsLoading &&
                            !tenantsError &&
                            tenants.length === 0 && (
                                <MessageBar
                                    messageBarType={MessageBarType.warning}
                                >
                                    No supported tenants are configured. Contact
                                    an SRM administrator.
                                </MessageBar>
                            )}
                        {!tenantsLoading &&
                            (tenantsError || tenants.length === 0) && (
                                <DefaultButton
                                    text="Reload tenants"
                                    iconProps={{ iconName: 'Refresh' }}
                                    onClick={() =>
                                        setTenantRefresh((value) => value + 1)
                                    }
                                />
                            )}
                        <Pivot
                            styles={{
                                root: { maxWidth: '100%', overflowX: 'auto' },
                            }}
                        >
                            <PivotItem
                                headerText="Check access"
                                itemIcon="Search"
                            >
                                <Stack
                                    tokens={{ childrenGap: 14 }}
                                    styles={{ root: { paddingTop: 16 } }}
                                >
                                    <Stack
                                        tokens={{ childrenGap: 12 }}
                                        styles={{ root: { maxWidth: 440 } }}
                                    >
                                        <TextField
                                            label="Application (client) ID"
                                            placeholder="00000000-0000-0000-0000-000000000000"
                                            value={lookupAppId}
                                            errorMessage={getGuidError(
                                                lookupAppId,
                                                'Application (client) ID'
                                            )}
                                            onChange={(_, v) =>
                                                setLookupAppId(v ?? '')
                                            }
                                        />
                                        <KustoTenantDropdown
                                            tenants={tenants}
                                            selectedTenantId={lookupTenantId}
                                            disabled={
                                                tenantsLoading ||
                                                !!tenantsError ||
                                                tenants.length === 0
                                            }
                                            onChange={setLookupTenantId}
                                        />
                                    </Stack>
                                    <Stack
                                        horizontal
                                        tokens={{ childrenGap: 12 }}
                                        verticalAlign="center"
                                    >
                                        <PrimaryButton
                                            text="Check access"
                                            iconProps={{
                                                iconName: 'Search',
                                            }}
                                            onClick={handleLookup}
                                            disabled={
                                                !lookupValid || lookupBusy
                                            }
                                        />
                                        {lookupBusy && (
                                            <Spinner
                                                size={SpinnerSize.small}
                                                label="Checking…"
                                                labelPosition="right"
                                            />
                                        )}
                                    </Stack>
                                    {lookupError && (
                                        <MessageBar
                                            messageBarType={
                                                MessageBarType.error
                                            }
                                        >
                                            {lookupError}
                                        </MessageBar>
                                    )}
                                    {lookupResult && !lookupBusy && (
                                        <MessageBar
                                            messageBarType={
                                                lookupResult.hasAccess
                                                    ? MessageBarType.success
                                                    : MessageBarType.warning
                                            }
                                        >
                                            {lookupResult.hasAccess
                                                ? `This application has access${
                                                      lookupResult.role
                                                          ? ` (${lookupResult.role})`
                                                          : ''
                                                  }${
                                                      lookupResult.displayName
                                                          ? ` — recorded as "${lookupResult.displayName}"`
                                                          : ''
                                                  }.`
                                                : 'This application does not currently have access to the database.'}
                                        </MessageBar>
                                    )}
                                </Stack>
                            </PivotItem>
                            <PivotItem
                                headerText="Request access"
                                itemIcon="Permissions"
                            >
                                <Stack
                                    tokens={{ childrenGap: 14 }}
                                    styles={{ root: { paddingTop: 16 } }}
                                >
                                    <Text
                                        variant="small"
                                        styles={{
                                            root: {
                                                color: theme.palette
                                                    .neutralSecondary,
                                            },
                                        }}
                                    >
                                        Kusto grants apply to app registrations
                                        (client IDs); managed identities must
                                        authenticate through an app
                                        registration.
                                    </Text>
                                    <Stack
                                        tokens={{ childrenGap: 12 }}
                                        styles={{ root: { maxWidth: 440 } }}
                                    >
                                        <TextField
                                            label="Application (client) ID"
                                            placeholder="00000000-0000-0000-0000-000000000000"
                                            value={reqAppId}
                                            required
                                            errorMessage={getGuidError(
                                                reqAppId,
                                                'Application (client) ID'
                                            )}
                                            onChange={(_, v) =>
                                                setReqAppId(v ?? '')
                                            }
                                        />
                                        <KustoTenantDropdown
                                            tenants={tenants}
                                            selectedTenantId={reqTenantId}
                                            required
                                            disabled={
                                                tenantsLoading ||
                                                !!tenantsError ||
                                                tenants.length === 0
                                            }
                                            onChange={setReqTenantId}
                                        />
                                        <TextField
                                            label="Application / service name"
                                            placeholder="e.g. Contoso Release Reporter"
                                            value={reqName}
                                            required
                                            maxLength={MAX_DISPLAY_NAME_LENGTH}
                                            onChange={(_, v) =>
                                                setReqName(v ?? '')
                                            }
                                        />
                                        <TextField
                                            label="Business justification"
                                            multiline
                                            rows={3}
                                            value={reqJustification}
                                            required
                                            maxLength={MAX_FREE_TEXT_LENGTH}
                                            onChange={(_, v) =>
                                                setReqJustification(v ?? '')
                                            }
                                        />
                                    </Stack>
                                    {reqName.trim().length > 0 &&
                                        !isValidDisplayName(reqName) && (
                                            <MessageBar
                                                messageBarType={
                                                    MessageBarType.warning
                                                }
                                            >
                                                Application name must be at most
                                                256 characters and may only
                                                contain letters, digits, spaces
                                                and the characters . _ - ( ) / ,
                                            </MessageBar>
                                        )}
                                    <Stack
                                        horizontal
                                        tokens={{ childrenGap: 12 }}
                                        verticalAlign="center"
                                    >
                                        <PrimaryButton
                                            text="Submit request"
                                            iconProps={{
                                                iconName: 'Send',
                                            }}
                                            onClick={handleSubmit}
                                            disabled={!requestValid || reqBusy}
                                        />
                                        {reqBusy && (
                                            <Spinner
                                                size={SpinnerSize.small}
                                                label="Submitting…"
                                                labelPosition="right"
                                            />
                                        )}
                                    </Stack>
                                    <Text
                                        variant="small"
                                        styles={{
                                            root: {
                                                color: theme.palette
                                                    .neutralSecondary,
                                            },
                                        }}
                                    >
                                        {
                                            'An SRM admin reviews your request. Track its status under "My requests".'
                                        }
                                    </Text>
                                    {reqError && (
                                        <MessageBar
                                            messageBarType={
                                                MessageBarType.error
                                            }
                                        >
                                            {reqError}
                                        </MessageBar>
                                    )}
                                    {reqMessage && (
                                        <MessageBar
                                            messageBarType={
                                                MessageBarType.success
                                            }
                                        >
                                            {reqMessage}
                                        </MessageBar>
                                    )}
                                </Stack>
                            </PivotItem>
                            <PivotItem
                                headerText="My requests"
                                itemIcon="History"
                                itemCount={myRequests.length}
                            >
                                <Stack
                                    tokens={{ childrenGap: 14 }}
                                    styles={{ root: { paddingTop: 16 } }}
                                >
                                    <Stack
                                        horizontal
                                        tokens={{ childrenGap: 12 }}
                                        verticalAlign="center"
                                    >
                                        <DefaultButton
                                            text="Refresh"
                                            iconProps={{
                                                iconName: 'Refresh',
                                            }}
                                            onClick={() => void loadMine()}
                                            disabled={mineBusy}
                                        />
                                        {mineBusy && (
                                            <Spinner
                                                size={SpinnerSize.small}
                                                label="Loading…"
                                                labelPosition="right"
                                            />
                                        )}
                                    </Stack>
                                    {mineError && (
                                        <MessageBar
                                            messageBarType={
                                                MessageBarType.error
                                            }
                                        >
                                            {mineError}
                                        </MessageBar>
                                    )}
                                    {!mineBusy && myRequests.length === 0 ? (
                                        <Text
                                            variant="medium"
                                            styles={{
                                                root: {
                                                    color: theme.palette
                                                        .neutralSecondary,
                                                },
                                            }}
                                        >
                                            You have not requested access for
                                            any application yet.
                                        </Text>
                                    ) : (
                                        <DetailsList
                                            items={myRequests}
                                            columns={myRequestColumns}
                                            selectionMode={SelectionMode.none}
                                            layoutMode={
                                                DetailsListLayoutMode.justified
                                            }
                                        />
                                    )}
                                </Stack>
                            </PivotItem>
                        </Pivot>
                    </SectionCard>
                </Stack>
            </div>

            <Dialog
                hidden={!cancelTarget}
                onDismiss={() => setCancelTarget(null)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Withdraw request?',
                    subText: cancelTarget
                        ? `Withdraw your ${cancelTarget.action?.toLowerCase()} request for "${
                              cancelTarget.displayName ??
                              cancelTarget.applicationId
                          }"? You can submit a new request later.`
                        : '',
                }}
            >
                <DialogFooter>
                    <PrimaryButton
                        text="Withdraw"
                        onClick={() => void confirmCancel()}
                        disabled={mineBusy}
                    />
                    <DefaultButton
                        text="Keep request"
                        onClick={() => setCancelTarget(null)}
                        disabled={mineBusy}
                    />
                </DialogFooter>
            </Dialog>

            <Dialog
                hidden={!removalTarget}
                onDismiss={() => setRemovalTarget(null)}
                minWidth={480}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Request removal of access?',
                    subText: removalTarget
                        ? `This asks an SRM admin to remove access for "${
                              removalTarget.displayName ??
                              removalTarget.applicationId
                          }". Anything still using this application will stop being able to query the database.`
                        : '',
                }}
            >
                <TextField
                    label="Why is this access no longer needed?"
                    multiline
                    rows={3}
                    required
                    value={removalJustification}
                    maxLength={MAX_FREE_TEXT_LENGTH}
                    onChange={(_, v) => setRemovalJustification(v ?? '')}
                />
                <DialogFooter>
                    <PrimaryButton
                        text="Submit removal request"
                        onClick={() => void confirmRemoval()}
                        disabled={
                            mineBusy || removalJustification.trim().length === 0
                        }
                    />
                    <DefaultButton
                        text="Cancel"
                        onClick={() => setRemovalTarget(null)}
                        disabled={mineBusy}
                    />
                </DialogFooter>
            </Dialog>
        </>
    );
};
